var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _hotKeys, _fullscreenElement, _keyUpHandler, keyUpHandler_fn, _keyDownHandler, keyDownHandler_fn;
import MediaContainer from "./media-container.js";
import { window, document } from "./utils/server-safe-globals.js";
import { AttributeTokenList } from "./utils/attribute-token-list.js";
import { fullscreenApi } from "./utils/fullscreenApi.js";
import { constToCamel } from "./utils/utils.js";
import { containsComposedNode } from "./utils/element-utils.js";
import { toggleSubsCaps } from "./utils/captions.js";
import {
  MediaUIEvents,
  MediaUIAttributes,
  MediaStateReceiverAttributes,
  TextTrackKinds,
  TextTrackModes,
  AvailabilityStates,
  AttributeToStateChangeEventMap,
  StreamTypes
} from "./constants.js";
const StreamTypeValues = Object.values(StreamTypes);
import {
  stringifyTextTrackList,
  getTextTracksList,
  updateTracksModeTo
} from "./utils/captions.js";
const ButtonPressedKeys = ["ArrowLeft", "ArrowRight", "Enter", " ", "f", "m", "k", "c"];
const DEFAULT_SEEK_OFFSET = 10;
const DEFAULT_TIME = 0;
class MediaController extends MediaContainer {
  constructor() {
    super();
    __privateAdd(this, _keyUpHandler);
    __privateAdd(this, _keyDownHandler);
    __privateAdd(this, _hotKeys, new AttributeTokenList(this, "hotkeys"));
    __privateAdd(this, _fullscreenElement, void 0);
    if (!airplaySupported) {
      this._airplayUnavailable = AvailabilityStates.UNSUPPORTED;
    }
    if (!fullscreenEnabled) {
      this._fullscreenUnavailable = AvailabilityStates.UNAVAILABLE;
    }
    if (!castSupported) {
      this._castUnavailable = AvailabilityStates.UNSUPPORTED;
    }
    if (!pipSupported) {
      this._pipUnavailable = AvailabilityStates.UNSUPPORTED;
    }
    if (volumeSupported !== void 0) {
      if (!volumeSupported) {
        this._volumeUnavailable = AvailabilityStates.UNSUPPORTED;
      }
    } else {
      volumeSupportPromise.then(() => {
        if (!volumeSupported) {
          this._volumeUnavailable = AvailabilityStates.UNSUPPORTED;
          this.propagateMediaState(MediaUIAttributes.MEDIA_VOLUME_UNAVAILABLE, this._volumeUnavailable);
        }
      });
    }
    this.mediaStateReceivers = [];
    this.associatedElementSubscriptions = /* @__PURE__ */ new Map();
    this.associateElement(this);
    const mediaUIEventHandlers = {
      MEDIA_PLAY_REQUEST: (e, media) => {
        const streamType = Delegates[MediaUIAttributes.MEDIA_STREAM_TYPE](this);
        const autoSeekToLive = this.getAttribute("noautoseektolive") === null;
        if (streamType == StreamTypes.LIVE && autoSeekToLive) {
          mediaUIEventHandlers["MEDIA_SEEK_TO_LIVE_REQUEST"](e, media);
        }
        this.media.play().catch(() => {
        });
      },
      MEDIA_PAUSE_REQUEST: () => this.media.pause(),
      MEDIA_MUTE_REQUEST: () => this.media.muted = true,
      MEDIA_UNMUTE_REQUEST: () => {
        const media = this.media;
        media.muted = false;
        if (media.volume === 0) {
          media.volume = 0.25;
        }
      },
      MEDIA_VOLUME_REQUEST: (e) => {
        const media = this.media;
        const volume = e.detail;
        media.volume = volume;
        if (volume > 0 && media.muted) {
          media.muted = false;
        }
        try {
          window.localStorage.setItem(
            "media-chrome-pref-volume",
            volume.toString()
          );
        } catch (err) {
        }
      },
      MEDIA_ENTER_FULLSCREEN_REQUEST: () => {
        if (!fullscreenEnabled) {
          console.warn("Fullscreen support is unavailable; not entering fullscreen");
          return;
        }
        const media = this.media;
        if (document.pictureInPictureElement) {
          document.exitPictureInPicture();
        }
        if (super[fullscreenApi.enter]) {
          this.fullscreenElement[fullscreenApi.enter]();
        } else if (media.webkitEnterFullscreen) {
          media.webkitEnterFullscreen();
        } else if (media.requestFullscreen) {
          media.requestFullscreen();
        } else {
          console.warn("MediaChrome: Fullscreen not supported");
        }
      },
      MEDIA_EXIT_FULLSCREEN_REQUEST: () => {
        document[fullscreenApi.exit]();
      },
      MEDIA_ENTER_PIP_REQUEST: () => {
        const media = this.media;
        if (!document.pictureInPictureEnabled) {
          console.warn("MediaChrome: Picture-in-picture is not enabled");
          return;
        }
        if (!media.requestPictureInPicture) {
          console.warn("MediaChrome: The current media does not support picture-in-picture");
          return;
        }
        if (document[fullscreenApi.element]) {
          document[fullscreenApi.exit]();
        }
        const warnNotReady = () => {
          console.warn("MediaChrome: The media is not ready for picture-in-picture. It must have a readyState > 0.");
        };
        media.requestPictureInPicture().catch((err) => {
          if (err.code === 11) {
            if (media.readyState === 0 && media.preload === "none") {
              const cleanup = () => {
                media.removeEventListener("loadedmetadata", tryPip);
                media.preload = "none";
              };
              const tryPip = () => {
                media.requestPictureInPicture().catch(warnNotReady);
                cleanup();
              };
              media.addEventListener("loadedmetadata", tryPip);
              media.preload = "metadata";
              setTimeout(() => {
                if (media.readyState === 0)
                  warnNotReady();
                cleanup();
              }, 1e3);
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        });
      },
      MEDIA_EXIT_PIP_REQUEST: () => {
        if (document.pictureInPictureElement) {
          document.exitPictureInPicture();
        }
      },
      MEDIA_ENTER_CAST_REQUEST: () => {
        var _a;
        const media = this.media;
        if (!((_a = globalThis.CastableVideoElement) == null ? void 0 : _a.castEnabled))
          return;
        if (document[fullscreenApi.element]) {
          document[fullscreenApi.exit]();
        }
        media.requestCast();
      },
      MEDIA_EXIT_CAST_REQUEST: async () => {
        var _a;
        if ((_a = globalThis.CastableVideoElement) == null ? void 0 : _a.castElement) {
          globalThis.CastableVideoElement.exitCast();
        }
      },
      MEDIA_SEEK_REQUEST: (e) => {
        const media = this.media;
        const time = e.detail;
        if (media.readyState > 0 || media.readyState === void 0) {
          media.currentTime = time;
        }
      },
      MEDIA_PLAYBACK_RATE_REQUEST: (e) => {
        this.media.playbackRate = e.detail;
      },
      MEDIA_PREVIEW_REQUEST: (e) => {
        var _a;
        const media = this.media;
        if (!media)
          return;
        const time = e.detail;
        if (time === null) {
          this.propagateMediaState(MediaUIAttributes.MEDIA_PREVIEW_TIME, void 0);
        }
        this.propagateMediaState(MediaUIAttributes.MEDIA_PREVIEW_TIME, time);
        const [track] = getTextTracksList(media, {
          kind: TextTrackKinds.METADATA,
          label: "thumbnails"
        });
        if (!(track && track.cues))
          return;
        if (time === null) {
          this.propagateMediaState(MediaUIAttributes.MEDIA_PREVIEW_IMAGE, void 0);
          this.propagateMediaState(MediaUIAttributes.MEDIA_PREVIEW_COORDS, void 0);
          return;
        }
        const cue = Array.prototype.find.call(
          track.cues,
          (c) => c.startTime >= time
        );
        if (!cue)
          return;
        const base = !/'^(?:[a-z]+:)?\/\//i.test(cue.text) ? (_a = media.querySelector('track[label="thumbnails"]')) == null ? void 0 : _a.src : void 0;
        const url = new URL(cue.text, base);
        const previewCoordsStr = new URLSearchParams(url.hash).get("#xywh");
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_PREVIEW_IMAGE,
          url.href
        );
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_PREVIEW_COORDS,
          previewCoordsStr.split(",").join(" ")
        );
      },
      MEDIA_SHOW_CAPTIONS_REQUEST: (e) => {
        const tracks = getCaptionTracks(this);
        const { detail: tracksToUpdate = [] } = e;
        updateTracksModeTo(TextTrackModes.SHOWING, tracks, tracksToUpdate);
      },
      MEDIA_DISABLE_CAPTIONS_REQUEST: (e) => {
        const tracks = getCaptionTracks(this);
        const { detail: tracksToUpdate = [] } = e;
        updateTracksModeTo(TextTrackModes.DISABLED, tracks, tracksToUpdate);
      },
      MEDIA_SHOW_SUBTITLES_REQUEST: (e) => {
        const tracks = getSubtitleTracks(this);
        const { detail: tracksToUpdate = [] } = e;
        updateTracksModeTo(TextTrackModes.SHOWING, tracks, tracksToUpdate);
      },
      MEDIA_DISABLE_SUBTITLES_REQUEST: (e) => {
        const tracks = getSubtitleTracks(this);
        const { detail: tracksToUpdate = [] } = e;
        updateTracksModeTo(TextTrackModes.DISABLED, tracks, tracksToUpdate);
      },
      MEDIA_AIRPLAY_REQUEST: () => {
        const { media } = this;
        if (!media)
          return;
        if (!(media.webkitShowPlaybackTargetPicker && window.WebKitPlaybackTargetAvailabilityEvent)) {
          console.warn(
            "received a request to select AirPlay but AirPlay is not supported in this environment"
          );
          return;
        }
        media.webkitShowPlaybackTargetPicker();
      },
      MEDIA_SEEK_TO_LIVE_REQUEST: (e, media) => {
        const seekable = media.seekable;
        if (!seekable) {
          console.warn("MediaController: Media element does not support seeking to live.");
          return;
        }
        if (!seekable.length) {
          console.warn("MediaController: Media is unable to seek to live.");
          return;
        }
        media.currentTime = seekable.end(seekable.length - 1);
      }
    };
    Object.keys(mediaUIEventHandlers).forEach((key) => {
      const handlerName = `_handle${constToCamel(key, true)}`;
      this[handlerName] = (e) => {
        e.stopPropagation();
        if (!this.media) {
          console.warn("MediaController: No media available.");
          return;
        }
        mediaUIEventHandlers[key](e, this.media);
      };
      this.addEventListener(MediaUIEvents[key], this[handlerName]);
    });
    this._mediaStatePropagators = {
      "play,pause,emptied": () => {
        this.propagateMediaState(MediaUIAttributes.MEDIA_PAUSED, getPaused(this));
      },
      "playing,emptied": () => {
        var _a;
        this.propagateMediaState(MediaUIAttributes.MEDIA_HAS_PLAYED, !((_a = this.media) == null ? void 0 : _a.paused));
      },
      volumechange: () => {
        this.propagateMediaState(MediaUIAttributes.MEDIA_MUTED, getMuted(this));
        this.propagateMediaState(MediaUIAttributes.MEDIA_VOLUME, getVolume(this));
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_VOLUME_LEVEL,
          getVolumeLevel(this)
        );
      },
      [fullscreenApi.event]: (e) => {
        const isSomeElementFullscreen = !!document[fullscreenApi.element];
        const fullscreenEl = isSomeElementFullscreen && (e == null ? void 0 : e.target);
        const isFullScreen = containsComposedNode(this.fullscreenElement, fullscreenEl);
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_IS_FULLSCREEN,
          isFullScreen
        );
      },
      "enterpictureinpicture,leavepictureinpicture": (e) => {
        var _a;
        let isPip;
        if (e) {
          isPip = e.type == "enterpictureinpicture";
        } else {
          const pipElement = (_a = this.getRootNode().pictureInPictureElement) != null ? _a : document.pictureInPictureElement;
          isPip = this.media && containsComposedNode(this.media, pipElement);
        }
        this.propagateMediaState(MediaUIAttributes.MEDIA_IS_PIP, isPip);
      },
      "entercast,leavecast,castchange": (e) => {
        var _a;
        const castElement = (_a = globalThis.CastableVideoElement) == null ? void 0 : _a.castElement;
        let castState = this.media && containsComposedNode(this.media, castElement);
        if ((e == null ? void 0 : e.type) === "castchange" && (e == null ? void 0 : e.detail) === "CONNECTING") {
          castState = "connecting";
        }
        this.propagateMediaState(MediaUIAttributes.MEDIA_IS_CASTING, castState);
      },
      "timeupdate,loadedmetadata": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_CURRENT_TIME,
          getCurrentTime(this)
        );
      },
      "durationchange,loadedmetadata,emptied": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_DURATION,
          getDuration(this)
        );
      },
      "emptied,durationchange,loadedmetadata,streamtypechange": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_STREAM_TYPE
        );
      },
      "emptied,durationchange,loadedmetadata,streamtypechange,targetlivewindowchange": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_TARGET_LIVE_WINDOW
        );
      },
      "loadedmetadata,emptied,progress": () => {
        var _a;
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_SEEKABLE,
          (_a = getSeekable(this)) == null ? void 0 : _a.join(":")
        );
      },
      "progress,emptied": () => {
        var _a;
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_BUFFERED,
          serializeTimeRanges((_a = this.media) == null ? void 0 : _a.buffered)
        );
      },
      "ratechange,loadstart": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_PLAYBACK_RATE,
          getPlaybackRate(this)
        );
      },
      "waiting,playing,emptied": () => {
        var _a;
        const isLoading = ((_a = this.media) == null ? void 0 : _a.readyState) < 3;
        this.propagateMediaState(MediaUIAttributes.MEDIA_LOADING, isLoading);
      },
      "playing,timeupdate,progress,waiting,emptied": () => {
        this.propagateMediaState(MediaUIAttributes.MEDIA_TIME_IS_LIVE);
      }
    };
    if (this._airplayUnavailable !== AvailabilityStates.UNSUPPORTED) {
      const airplaySupporHandler = (event) => {
        if ((event == null ? void 0 : event.availability) === "available") {
          this._airplayUnavailable = void 0;
        } else if ((event == null ? void 0 : event.availability) === "not-available") {
          this._airplayUnavailable = AvailabilityStates.UNAVAILABLE;
        }
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_AIRPLAY_UNAVAILABLE,
          this._airplayUnavailable
        );
      };
      this._mediaStatePropagators["webkitplaybacktargetavailabilitychanged"] = airplaySupporHandler;
    }
    if (this._castUnavailable !== AvailabilityStates.UNSUPPORTED) {
      const castSupportHandler = () => {
        var _a;
        const castState = (_a = globalThis.CastableVideoElement) == null ? void 0 : _a.castState;
        if (castState == null ? void 0 : castState.includes("CONNECT")) {
          this._castUnavailable = void 0;
        } else {
          this._castUnavailable = AvailabilityStates.UNAVAILABLE;
        }
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_CAST_UNAVAILABLE,
          this._castUnavailable
        );
      };
      this._mediaStatePropagators["castchange"] = castSupportHandler;
    }
    this._textTrackMediaStatePropagators = {
      "addtrack,removetrack,loadstart": () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_CAPTIONS_LIST,
          stringifyTextTrackList(getCaptionTracks(this)) || void 0
        );
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_SUBTITLES_LIST,
          stringifyTextTrackList(getSubtitleTracks(this)) || void 0
        );
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_CAPTIONS_SHOWING,
          stringifyTextTrackList(getShowingCaptionTracks(this)) || void 0
        );
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_SUBTITLES_SHOWING,
          stringifyTextTrackList(getShowingSubtitleTracks(this)) || void 0
        );
      },
      change: () => {
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_CAPTIONS_SHOWING,
          stringifyTextTrackList(getShowingCaptionTracks(this)) || void 0
        );
        this.propagateMediaState(
          MediaUIAttributes.MEDIA_SUBTITLES_SHOWING,
          stringifyTextTrackList(getShowingSubtitleTracks(this)) || void 0
        );
      }
    };
    this.enableHotkeys();
  }
  static get observedAttributes() {
    return super.observedAttributes.concat("nohotkeys", "hotkeys", "default-stream-type");
  }
  get fullscreenElement() {
    var _a;
    return (_a = __privateGet(this, _fullscreenElement)) != null ? _a : this;
  }
  set fullscreenElement(element) {
    if (this.hasAttribute("fullscreen-element")) {
      this.removeAttribute("fullscreen-element");
    }
    __privateSet(this, _fullscreenElement, element);
  }
  attributeChangedCallback(attrName, oldValue, newValue) {
    var _a;
    if (attrName === "nohotkeys") {
      if (newValue !== oldValue && newValue === "") {
        if (this.hasAttribute("hotkeys")) {
          console.warn("Both `hotkeys` and `nohotkeys` have been set. All hotkeys will be disabled.");
        }
        this.disableHotkeys();
      } else if (newValue !== oldValue && newValue === null) {
        this.enableHotkeys();
      }
    } else if (attrName === "hotkeys") {
      __privateGet(this, _hotKeys).value = newValue;
    } else if (attrName === "default-stream-type") {
      this.propagateMediaState(MediaUIAttributes.MEDIA_STREAM_TYPE);
    } else if (attrName === "fullscreen-element") {
      const el = newValue ? (_a = this.getRootNode()) == null ? void 0 : _a.getElementById(newValue) : void 0;
      __privateSet(this, _fullscreenElement, el);
    }
    super.attributeChangedCallback(attrName, oldValue, newValue);
  }
  mediaSetCallback(media) {
    super.mediaSetCallback(media);
    if (!media.hasAttribute("tabindex")) {
      media.setAttribute("tabindex", -1);
    }
    Object.keys(this._mediaStatePropagators).forEach((key) => {
      const events = key.split(",");
      const handler = this._mediaStatePropagators[key];
      events.forEach((event) => {
        const target = event == fullscreenApi.event ? this.getRootNode() : media;
        target.addEventListener(event, handler);
      });
      handler();
    });
    Object.entries(this._textTrackMediaStatePropagators).forEach(
      ([eventsStr, handler]) => {
        const events = eventsStr.split(",");
        events.forEach((event) => {
          if (media.textTracks) {
            media.textTracks.addEventListener(event, handler);
          }
        });
        handler();
      }
    );
    try {
      const volPref = window.localStorage.getItem("media-chrome-pref-volume");
      if (volPref !== null)
        media.volume = volPref;
    } catch (e) {
      console.debug("Error getting volume pref", e);
    }
  }
  mediaUnsetCallback(media) {
    super.mediaUnsetCallback(media);
    Object.keys(this._mediaStatePropagators).forEach((key) => {
      const events = key.split(",");
      const handler = this._mediaStatePropagators[key];
      events.forEach((event) => {
        const target = event == fullscreenApi.event ? this.getRootNode() : media;
        target.removeEventListener(event, handler);
      });
    });
    Object.entries(this._textTrackMediaStatePropagators).forEach(
      ([eventsStr, handler]) => {
        const events = eventsStr.split(",");
        events.forEach((event) => {
          if (media.textTracks) {
            media.textTracks.removeEventListener(event, handler);
          }
        });
        handler();
      }
    );
    this.propagateMediaState(MediaUIAttributes.MEDIA_PAUSED, true);
  }
  propagateMediaState(stateName, state) {
    if (arguments.length === 1) {
      state = Delegates[stateName](this);
    }
    const previousState = this.getAttribute(stateName);
    propagateMediaState(this.mediaStateReceivers, stateName, state);
    if (previousState === this.getAttribute(stateName))
      return;
    const eventName = AttributeToStateChangeEventMap[stateName];
    if (!eventName)
      return;
    const evt = new window.CustomEvent(eventName, { composed: true, bubbles: true, detail: state });
    this.dispatchEvent(evt);
  }
  associateElement(element) {
    if (!element)
      return;
    const { associatedElementSubscriptions } = this;
    if (associatedElementSubscriptions.has(element))
      return;
    const registerMediaStateReceiver = this.registerMediaStateReceiver.bind(this);
    const unregisterMediaStateReceiver = this.unregisterMediaStateReceiver.bind(this);
    const unsubscribe = monitorForMediaStateReceivers(
      element,
      registerMediaStateReceiver,
      unregisterMediaStateReceiver
    );
    Object.keys(MediaUIEvents).forEach((key) => {
      element.addEventListener(
        MediaUIEvents[key],
        this[`_handle${constToCamel(key, true)}`]
      );
    });
    associatedElementSubscriptions.set(element, unsubscribe);
  }
  unassociateElement(element) {
    if (!element)
      return;
    const { associatedElementSubscriptions } = this;
    if (!associatedElementSubscriptions.has(element))
      return;
    const unsubscribe = associatedElementSubscriptions.get(element);
    unsubscribe();
    associatedElementSubscriptions.delete(element);
    Object.keys(MediaUIEvents).forEach((key) => {
      element.removeEventListener(
        MediaUIEvents[key],
        this[`_handle${constToCamel(key, true)}`]
      );
    });
  }
  registerMediaStateReceiver(el) {
    if (!el)
      return;
    const els = this.mediaStateReceivers;
    const index = els.indexOf(el);
    if (index > -1)
      return;
    els.push(el);
    propagateMediaState(
      [el],
      MediaUIAttributes.MEDIA_VOLUME_UNAVAILABLE,
      this._volumeUnavailable
    );
    propagateMediaState(
      [el],
      MediaUIAttributes.MEDIA_AIRPLAY_UNAVAILABLE,
      this._airplayUnavailable
    );
    propagateMediaState(
      [el],
      MediaUIAttributes.MEDIA_FULLSCREEN_UNAVAILABLE,
      this._fullscreenUnavailable
    );
    propagateMediaState(
      [el],
      MediaUIAttributes.MEDIA_CAST_UNAVAILABLE,
      this._castUnavailable
    );
    propagateMediaState(
      [el],
      MediaUIAttributes.MEDIA_PIP_UNAVAILABLE,
      this._pipUnavailable
    );
    if (this.media) {
      Object.keys(MediaUIAttributes).forEach((attribute) => {
        if (attribute.includes("UNAVAILABLE"))
          return;
        propagateMediaState(
          [el],
          MediaUIAttributes[attribute],
          Delegates[MediaUIAttributes[attribute]] ? Delegates[MediaUIAttributes[attribute]](this) : Delegates.default(this, MediaUIAttributes[attribute])
        );
      });
    }
  }
  unregisterMediaStateReceiver(el) {
    const els = this.mediaStateReceivers;
    const index = els.indexOf(el);
    if (index < 0)
      return;
    els.splice(index, 1);
  }
  enableHotkeys() {
    this.addEventListener("keydown", __privateMethod(this, _keyDownHandler, keyDownHandler_fn));
  }
  disableHotkeys() {
    this.removeEventListener("keydown", __privateMethod(this, _keyDownHandler, keyDownHandler_fn));
    this.removeEventListener("keyup", __privateMethod(this, _keyUpHandler, keyUpHandler_fn));
  }
  get hotkeys() {
    return __privateGet(this, _hotKeys);
  }
  keyboardShortcutHandler(e) {
    var _a, _b, _c, _d;
    const keysUsed = ((_d = (_c = (_a = e.target.getAttribute("keysused")) == null ? void 0 : _a.split(" ")) != null ? _c : (_b = e.target) == null ? void 0 : _b.keysUsed) != null ? _d : []).map((key) => key === "Space" ? " " : key).filter(Boolean);
    if (keysUsed.includes(e.key)) {
      return;
    }
    let eventName, currentTimeStr, currentTime, detail, evt;
    const seekOffset = DEFAULT_SEEK_OFFSET;
    if (__privateGet(this, _hotKeys).contains(`no${e.key.toLowerCase()}`))
      return;
    if (e.key === " " && __privateGet(this, _hotKeys).contains(`nospace`))
      return;
    switch (e.key) {
      case " ":
      case "k":
        eventName = this.getAttribute(MediaUIAttributes.MEDIA_PAUSED) != null ? MediaUIEvents.MEDIA_PLAY_REQUEST : MediaUIEvents.MEDIA_PAUSE_REQUEST;
        this.dispatchEvent(
          new window.CustomEvent(eventName, { composed: true, bubbles: true })
        );
        break;
      case "m":
        eventName = this.getAttribute(MediaUIAttributes.MEDIA_VOLUME_LEVEL) === "off" ? MediaUIEvents.MEDIA_UNMUTE_REQUEST : MediaUIEvents.MEDIA_MUTE_REQUEST;
        this.dispatchEvent(
          new window.CustomEvent(eventName, { composed: true, bubbles: true })
        );
        break;
      case "f":
        eventName = this.getAttribute(MediaUIAttributes.MEDIA_IS_FULLSCREEN) != null ? MediaUIEvents.MEDIA_EXIT_FULLSCREEN_REQUEST : MediaUIEvents.MEDIA_ENTER_FULLSCREEN_REQUEST;
        this.dispatchEvent(
          new window.CustomEvent(eventName, { composed: true, bubbles: true })
        );
        break;
      case "c":
        toggleSubsCaps(this);
        break;
      case "ArrowLeft":
        currentTimeStr = this.getAttribute(
          MediaUIAttributes.MEDIA_CURRENT_TIME
        );
        currentTime = currentTimeStr && !Number.isNaN(+currentTimeStr) ? +currentTimeStr : DEFAULT_TIME;
        detail = Math.max(currentTime - seekOffset, 0);
        evt = new window.CustomEvent(MediaUIEvents.MEDIA_SEEK_REQUEST, {
          composed: true,
          bubbles: true,
          detail
        });
        this.dispatchEvent(evt);
        break;
      case "ArrowRight":
        currentTimeStr = this.getAttribute(
          MediaUIAttributes.MEDIA_CURRENT_TIME
        );
        currentTime = currentTimeStr && !Number.isNaN(+currentTimeStr) ? +currentTimeStr : DEFAULT_TIME;
        detail = Math.max(currentTime + seekOffset, 0);
        evt = new window.CustomEvent(MediaUIEvents.MEDIA_SEEK_REQUEST, {
          composed: true,
          bubbles: true,
          detail
        });
        this.dispatchEvent(evt);
        break;
      default:
        break;
    }
  }
}
_hotKeys = new WeakMap();
_fullscreenElement = new WeakMap();
_keyUpHandler = new WeakSet();
keyUpHandler_fn = function(e) {
  const { key } = e;
  if (!ButtonPressedKeys.includes(key)) {
    this.removeEventListener("keyup", __privateMethod(this, _keyUpHandler, keyUpHandler_fn));
    return;
  }
  this.keyboardShortcutHandler(e);
};
_keyDownHandler = new WeakSet();
keyDownHandler_fn = function(e) {
  const { metaKey, altKey, key } = e;
  if (metaKey || altKey || !ButtonPressedKeys.includes(key)) {
    this.removeEventListener("keyup", __privateMethod(this, _keyUpHandler, keyUpHandler_fn));
    return;
  }
  if ([" ", "ArrowLeft", "ArrowRight"].includes(key) && !(__privateGet(this, _hotKeys).contains(`no${key.toLowerCase()}`) || key === " " && __privateGet(this, _hotKeys).contains("nospace"))) {
    e.preventDefault();
  }
  this.addEventListener("keyup", __privateMethod(this, _keyUpHandler, keyUpHandler_fn), { once: true });
};
const Delegates = {
  default(el, attribute) {
    return el.getAttribute(attribute);
  },
  [MediaUIAttributes.MEDIA_CAPTIONS_LIST](el) {
    return stringifyTextTrackList(getCaptionTracks(el)) || void 0;
  },
  [MediaUIAttributes.MEDIA_SUBTITLES_LIST](el) {
    return stringifyTextTrackList(getSubtitleTracks(el)) || void 0;
  },
  [MediaUIAttributes.MEDIA_SUBTITLES_LIST](el) {
    return stringifyTextTrackList(getSubtitleTracks(el)) || void 0;
  },
  [MediaUIAttributes.MEDIA_CAPTIONS_SHOWING](el) {
    return stringifyTextTrackList(getShowingCaptionTracks(el)) || void 0;
  },
  [MediaUIAttributes.MEDIA_PAUSED](el) {
    return getPaused(el);
  },
  [MediaUIAttributes.MEDIA_MUTED](el) {
    return getMuted(el);
  },
  [MediaUIAttributes.MEDIA_VOLUME](el) {
    return getVolume(el);
  },
  [MediaUIAttributes.MEDIA_VOLUME_LEVEL](el) {
    return getVolumeLevel(el);
  },
  [MediaUIAttributes.MEDIA_CURRENT_TIME](el) {
    return getCurrentTime(el);
  },
  [MediaUIAttributes.MEDIA_DURATION](el) {
    return getDuration(el);
  },
  [MediaUIAttributes.MEDIA_SEEKABLE](el) {
    var _a;
    return (_a = getSeekable(el)) == null ? void 0 : _a.join(":");
  },
  [MediaUIAttributes.MEDIA_PLAYBACK_RATE](el) {
    return getPlaybackRate(el);
  },
  [MediaUIAttributes.MEDIA_STREAM_TYPE](el) {
    return getStreamType(el);
  },
  [MediaUIAttributes.MEDIA_TARGET_LIVE_WINDOW](el) {
    return getTargetLiveWindow(el);
  },
  [MediaUIAttributes.MEDIA_TIME_IS_LIVE](controller) {
    const media = controller.media;
    if (!media)
      return false;
    if (typeof media.liveEdgeStart === "number") {
      if (Number.isNaN(media.liveEdgeStart))
        return false;
      return media.currentTime >= media.liveEdgeStart;
    }
    const live = getStreamType(controller) === "live";
    if (!live)
      return false;
    const seekable = media.seekable;
    if (!seekable)
      return true;
    if (!seekable.length)
      return false;
    const liveEdgeStartOffset = controller.hasAttribute("liveedgeoffset") ? Number(controller.getAttribute("liveedgeoffset")) : 10;
    const liveEdgeStart = seekable.end(seekable.length - 1) - liveEdgeStartOffset;
    return media.currentTime >= liveEdgeStart;
  }
};
const getPaused = (controller) => {
  if (!controller.media)
    return true;
  return controller.media.paused;
};
const getMuted = (controller) => {
  return !!(controller.media && controller.media.muted);
};
const getVolume = (controller) => {
  const media = controller.media;
  return media ? media.volume : 1;
};
const getVolumeLevel = (controller) => {
  let level = "high";
  if (!controller.media)
    return level;
  const { muted, volume } = controller.media;
  if (volume === 0 || muted) {
    level = "off";
  } else if (volume < 0.5) {
    level = "low";
  } else if (volume < 0.75) {
    level = "medium";
  }
  return level;
};
const getCurrentTime = (controller) => {
  const media = controller.media;
  return media ? media.currentTime : 0;
};
const getDuration = (controller) => {
  const media = controller == null ? void 0 : controller.media;
  if (!Number.isFinite(media == null ? void 0 : media.duration))
    return NaN;
  return media.duration;
};
const getSeekable = (controller) => {
  var _a;
  const media = controller == null ? void 0 : controller.media;
  if (!((_a = media == null ? void 0 : media.seekable) == null ? void 0 : _a.length))
    return void 0;
  const start = media.seekable.start(0);
  const end = media.seekable.end(media.seekable.length - 1);
  if (!start && !end)
    return void 0;
  return [Number(start.toFixed(3)), Number(end.toFixed(3))];
};
const getPlaybackRate = (controller) => {
  const media = controller.media;
  return media ? media.playbackRate : 1;
};
const getSubtitleTracks = (controller) => {
  return getTextTracksList(controller.media, { kind: TextTrackKinds.SUBTITLES });
};
const getCaptionTracks = (controller) => {
  return getTextTracksList(controller.media, { kind: TextTrackKinds.CAPTIONS });
};
const getShowingSubtitleTracks = (controller) => {
  return getTextTracksList(controller.media, {
    kind: TextTrackKinds.SUBTITLES,
    mode: TextTrackModes.SHOWING
  });
};
const getShowingCaptionTracks = (controller) => {
  return getTextTracksList(controller.media, {
    kind: TextTrackKinds.CAPTIONS,
    mode: TextTrackModes.SHOWING
  });
};
const getStreamType = (controller) => {
  const { media } = controller;
  if (!media)
    return void 0;
  const { streamType } = media;
  if (StreamTypeValues.includes(streamType)) {
    if (streamType === StreamTypes.UNKNOWN) {
      const defaultType = controller.getAttribute("default-stream-type");
      if ([StreamTypes.LIVE, StreamTypes.ON_DEMAND].includes(defaultType)) {
        return defaultType;
      }
      return void 0;
    }
    return streamType;
  }
  const duration = media.duration;
  if (duration === Infinity) {
    return StreamTypes.LIVE;
  } else if (Number.isFinite(duration)) {
    return StreamTypes.ON_DEMAND;
  } else {
    const defaultType = controller.getAttribute("default-stream-type");
    if ([StreamTypes.LIVE, StreamTypes.ON_DEMAND].includes(defaultType)) {
      return defaultType;
    }
  }
  return void 0;
};
const getTargetLiveWindow = (controller) => {
  const { media } = controller;
  if (!media)
    return Number.NaN;
  const { targetLiveWindow } = media;
  if ((targetLiveWindow == null || Number.isNaN(targetLiveWindow)) && getStreamType(controller) === StreamTypes.LIVE) {
    return 0;
  }
  return targetLiveWindow;
};
const MEDIA_UI_ATTRIBUTE_NAMES = Object.values(MediaUIAttributes);
const getMediaUIAttributesFrom = (child) => {
  var _a, _b, _c, _d;
  let { observedAttributes } = child.constructor;
  if (!observedAttributes && ((_a = child.nodeName) == null ? void 0 : _a.includes("-"))) {
    window.customElements.upgrade(child);
    ({ observedAttributes } = child.constructor);
  }
  const mediaChromeAttributesList = (_d = (_c = (_b = child == null ? void 0 : child.getAttribute) == null ? void 0 : _b.call(child, MediaStateReceiverAttributes.MEDIA_CHROME_ATTRIBUTES)) == null ? void 0 : _c.split) == null ? void 0 : _d.call(_c, /\s+/);
  if (!Array.isArray(observedAttributes || mediaChromeAttributesList))
    return [];
  return (observedAttributes || mediaChromeAttributesList).filter(
    (attrName) => MEDIA_UI_ATTRIBUTE_NAMES.includes(attrName)
  );
};
const isMediaStateReceiver = (child) => {
  return !!getMediaUIAttributesFrom(child).length;
};
const setAttr = async (child, attrName, attrValue) => {
  if (!child.isConnected) {
    await delay(0);
  }
  if (attrValue == void 0) {
    return child.removeAttribute(attrName);
  }
  if (typeof attrValue === "boolean") {
    if (attrValue)
      return child.setAttribute(attrName, "");
    return child.removeAttribute(attrName);
  }
  if (Number.isNaN(attrValue)) {
    return child.removeAttribute(attrName);
  }
  return child.setAttribute(attrName, attrValue);
};
const isMediaSlotElementDescendant = (el) => {
  var _a;
  return !!((_a = el.closest) == null ? void 0 : _a.call(el, '*[slot="media"]'));
};
const traverseForMediaStateReceivers = (rootNode, mediaStateReceiverCallback) => {
  if (isMediaSlotElementDescendant(rootNode)) {
    return;
  }
  const traverseForMediaStateReceiversSync = (rootNode2, mediaStateReceiverCallback2) => {
    var _a, _b;
    if (isMediaStateReceiver(rootNode2)) {
      mediaStateReceiverCallback2(rootNode2);
    }
    const { children = [] } = rootNode2 != null ? rootNode2 : {};
    const shadowChildren = (_b = (_a = rootNode2 == null ? void 0 : rootNode2.shadowRoot) == null ? void 0 : _a.children) != null ? _b : [];
    const allChildren = [...children, ...shadowChildren];
    allChildren.forEach(
      (child) => traverseForMediaStateReceivers(child, mediaStateReceiverCallback2)
    );
  };
  const name = rootNode == null ? void 0 : rootNode.nodeName.toLowerCase();
  if (name.includes("-") && !isMediaStateReceiver(rootNode)) {
    window.customElements.whenDefined(name).then(() => {
      traverseForMediaStateReceiversSync(rootNode, mediaStateReceiverCallback);
    });
    return;
  }
  traverseForMediaStateReceiversSync(rootNode, mediaStateReceiverCallback);
};
const propagateMediaState = (els, stateName, val) => {
  els.forEach((el) => {
    const relevantAttrs = getMediaUIAttributesFrom(el);
    if (!relevantAttrs.includes(stateName))
      return;
    setAttr(el, stateName, val);
  });
};
const monitorForMediaStateReceivers = (rootNode, registerMediaStateReceiver, unregisterMediaStateReceiver) => {
  traverseForMediaStateReceivers(rootNode, registerMediaStateReceiver);
  const registerMediaStateReceiverHandler = (evt) => {
    var _a;
    const el = (_a = evt == null ? void 0 : evt.composedPath()[0]) != null ? _a : evt.target;
    registerMediaStateReceiver(el);
  };
  const unregisterMediaStateReceiverHandler = (evt) => {
    var _a;
    const el = (_a = evt == null ? void 0 : evt.composedPath()[0]) != null ? _a : evt.target;
    unregisterMediaStateReceiver(el);
  };
  rootNode.addEventListener(
    MediaUIEvents.REGISTER_MEDIA_STATE_RECEIVER,
    registerMediaStateReceiverHandler
  );
  rootNode.addEventListener(
    MediaUIEvents.UNREGISTER_MEDIA_STATE_RECEIVER,
    unregisterMediaStateReceiverHandler
  );
  const mutationCallback = (mutationsList) => {
    mutationsList.forEach((mutationRecord) => {
      const {
        addedNodes = [],
        removedNodes = [],
        type,
        target,
        attributeName
      } = mutationRecord;
      if (type === "childList") {
        Array.prototype.forEach.call(
          addedNodes,
          (node) => traverseForMediaStateReceivers(node, registerMediaStateReceiver)
        );
        Array.prototype.forEach.call(
          removedNodes,
          (node) => traverseForMediaStateReceivers(node, unregisterMediaStateReceiver)
        );
      } else if (type === "attributes" && attributeName === MediaStateReceiverAttributes.MEDIA_CHROME_ATTRIBUTES) {
        if (isMediaStateReceiver(target)) {
          registerMediaStateReceiver(target);
        } else {
          unregisterMediaStateReceiver(target);
        }
      }
    });
  };
  const observer = new MutationObserver(mutationCallback);
  observer.observe(rootNode, {
    childList: true,
    attributes: true,
    subtree: true
  });
  const unsubscribe = () => {
    traverseForMediaStateReceivers(rootNode, unregisterMediaStateReceiver);
    observer.disconnect();
    rootNode.removeEventListener(
      MediaUIEvents.REGISTER_MEDIA_STATE_RECEIVER,
      registerMediaStateReceiverHandler
    );
    rootNode.removeEventListener(
      MediaUIEvents.UNREGISTER_MEDIA_STATE_RECEIVER,
      unregisterMediaStateReceiverHandler
    );
  };
  return unsubscribe;
};
let testMediaEl;
const getTestMediaEl = () => {
  var _a, _b;
  if (testMediaEl)
    return testMediaEl;
  testMediaEl = (_b = (_a = document) == null ? void 0 : _a.createElement) == null ? void 0 : _b.call(_a, "video");
  return testMediaEl;
};
const hasVolumeSupportAsync = async (mediaEl = getTestMediaEl()) => {
  if (!mediaEl)
    return false;
  const prevVolume = mediaEl.volume;
  mediaEl.volume = prevVolume / 2 + 0.1;
  await delay(0);
  return mediaEl.volume !== prevVolume;
};
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const hasPipSupport = (mediaEl = getTestMediaEl()) => typeof (mediaEl == null ? void 0 : mediaEl.requestPictureInPicture) === "function";
const pipSupported = hasPipSupport();
let volumeSupported;
const volumeSupportPromise = hasVolumeSupportAsync().then((supported) => {
  volumeSupported = supported;
  return volumeSupported;
});
const airplaySupported = !!window.WebKitPlaybackTargetAvailabilityEvent;
const castSupported = !!window.chrome;
const hasFullscreenSupport = (mediaEl = getTestMediaEl()) => {
  let fullscreenEnabled2 = document[fullscreenApi.enabled];
  if (!fullscreenEnabled2 && mediaEl) {
    fullscreenEnabled2 = "webkitSupportsFullscreen" in mediaEl;
  }
  return fullscreenEnabled2;
};
const fullscreenEnabled = hasFullscreenSupport();
const emptyTimeRanges = Object.freeze({
  length: 0,
  start(index) {
    const unsignedIdx = index >>> 0;
    if (unsignedIdx >= this.length) {
      throw new DOMException(
        `Failed to execute 'start' on 'TimeRanges': The index provided (${unsignedIdx}) is greater than or equal to the maximum bound (${this.length}).`
      );
    }
    return 0;
  },
  end(index) {
    const unsignedIdx = index >>> 0;
    if (unsignedIdx >= this.length) {
      throw new DOMException(
        `Failed to execute 'end' on 'TimeRanges': The index provided (${unsignedIdx}) is greater than or equal to the maximum bound (${this.length}).`
      );
    }
    return 0;
  }
});
function serializeTimeRanges(timeRanges = emptyTimeRanges) {
  return Array.from(timeRanges).map((_, i) => [
    Number(timeRanges.start(i).toFixed(3)),
    Number(timeRanges.end(i).toFixed(3))
  ].join(":")).join(" ");
}
if (!window.customElements.get("media-controller")) {
  window.customElements.define("media-controller", MediaController);
}
var media_controller_default = MediaController;
export {
  media_controller_default as default,
  delay,
  getTestMediaEl,
  hasFullscreenSupport,
  hasPipSupport,
  hasVolumeSupportAsync
};
